import React, { useEffect } from 'react';
import { Form, Row, Col, Card, Accordion } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faLock } from '@fortawesome/free-solid-svg-icons';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import { useEmpresas } from 'providers/EmpresaProvider';
import { useToast } from 'providers/ToastProvider';
import { useCriarRascunhoNFe } from 'hooks/useNFe';
import NaturezaOperacaoInput from '../components/NaturezaOperacaoInput';
import '../nfe.css';

const breadcrumbItems = [
  { label: 'Operacional', url: '#!' },
  { label: 'Notas Fiscais', url: '/operacional/nfe' },
  { label: 'Nova NF-e', active: true },
];

const LOCKED_SECTIONS = [
  { num: '2', label: 'Destinatário' },
  { num: '3', label: 'Itens' },
  { num: '4', label: 'Transporte' },
  { num: '5', label: 'Duplicatas' },
  { num: '6', label: 'Informações Adicionais' },
  { num: '7', label: 'Referências NF-e' },
  { num: '8', label: 'Totais' },
];

interface FormValues {
  empresaId: string;
  serie: number;
  modelo: string;
  naturezaOperacao: string;
  finalidadeNFe: string;
  tipoNota: string;
  indicadorDestinatario: string;
  indicadorFinal: string;
  indicadorPresenca: string;
}

const NovaNFePage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { empresas, selectedIds } = useEmpresas();
  const criarMutation = useCriarRascunhoNFe();

  const empresa = empresas?.find(e => e.id === selectedIds[0]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      empresaId: empresa?.id ?? '',
      serie: empresa?.serieNFe ?? 1,
      modelo: '55',
      naturezaOperacao: '',
      finalidadeNFe: '1',
      tipoNota: '1',
      indicadorDestinatario: '',
      indicadorFinal: '1',
      indicadorPresenca: '1',
    },
  });

  useEffect(() => {
    if (empresa) {
      reset(prev => ({
        ...prev,
        empresaId: empresa.id,
        serie: empresa.serieNFe ?? 1,
      }));
    }
  }, [empresa, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await criarMutation.mutateAsync({
        empresaId: data.empresaId,
        serie: Number(data.serie),
        modelo: data.modelo,
        naturezaOperacao: data.naturezaOperacao,
        finalidadeNFe: data.finalidadeNFe,
        tipoNota: data.tipoNota,
        indicadorDestinatario: data.indicadorDestinatario || undefined,
        indicadorFinal: data.indicadorFinal || undefined,
        indicadorPresenca: data.indicadorPresenca || undefined,
        tipoEmissao: '1',
      });
      navigate(`/operacional/nfe/${response.emissaoId}`);
    } catch {
      showToast('Erro ao criar rascunho. Tente novamente.', 'error');
    }
  };

  const isPending = criarMutation.isPending;

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />

      <Row className="g-3">
        <Col xs={12}>
          <Button
            variant="phoenix-secondary"
            size="sm"
            onClick={() => navigate('/operacional/nfe')}
            disabled={isPending}
            className="d-inline-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Voltar para lista
          </Button>
        </Col>

        <Col xs={12}>
          <div className="nfe-doc-header">
            <div className="nfe-doc-header__bg-text">NF-e</div>
            <div className="d-flex align-items-center flex-wrap gap-3">
              <div className="flex-grow-1">
                <div className="nfe-doc-header__kicker">Nota Fiscal Eletrônica</div>
                <div className="nfe-doc-header__number">Nova NF-e</div>
                <div className="nfe-doc-header__meta">
                  Preencha a identificação para criar o rascunho
                </div>
              </div>
              <div className="nfe-doc-header__divider d-none d-md-block" />
              <div>
                <div className="nfe-doc-header__field-label">Emitente</div>
                <div className="nfe-doc-header__field-value">
                  {empresa?.nomeFantasia ?? '—'}
                </div>
              </div>
              <div className="nfe-doc-header__divider d-none d-lg-block" />
              <div className="text-end">
                <span className="nfe-badge nfe-badge--rascunho">Novo</span>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12}>
          <Card className="border border-opacity-25 shadow-sm overflow-hidden">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Accordion
                activeKey="cabecalho"
                onSelect={() => {}}
                className="nfe-accordion"
              >
                <Accordion.Item eventKey="cabecalho" className="active-section">
                  <Accordion.Header>
                    <span className="nfe-sec-num">1</span>
                    Identificação
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <label className="nfe-dest-label">Empresa *</label>
                          <Form.Select size="sm" {...register('empresaId', { required: true })} disabled={isPending}>
                            {empresas?.map(e => (
                              <option key={e.id} value={e.id}>{e.nomeFantasia}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={2}>
                        <Form.Group>
                          <label className="nfe-dest-label">Modelo *</label>
                          <Form.Select size="sm" {...register('modelo')} disabled={isPending}>
                            <option value="55">55 — NF-e</option>
                            <option value="65">65 — NFC-e</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={2}>
                        <Form.Group>
                          <label className="nfe-dest-label">Série *</label>
                          <Form.Control
                            size="sm"
                            type="number"
                            {...register('serie', { required: true, min: 1, max: 999 })}
                            disabled={isPending}
                            min={1}
                            max={999}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={10}>
                        <Form.Group>
                          <label className="nfe-dest-label">Natureza da Operação *</label>
                          <Controller
                            name="naturezaOperacao"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <NaturezaOperacaoInput
                                {...field}
                                size="sm"
                                isInvalid={!!errors.naturezaOperacao}
                                disabled={isPending}
                                placeholder="Ex: Venda de mercadoria"
                                autoFocus
                              />
                            )}
                          />
                          {errors.naturezaOperacao && (
                            <div className="invalid-feedback d-block">Obrigatório.</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="g-3 mt-0">
                      <Col xs={12} sm={6} md={3}>
                        <Form.Group>
                          <label className="nfe-dest-label">Finalidade *</label>
                          <Form.Select size="sm" {...register('finalidadeNFe')} disabled={isPending}>
                            <option value="1">1 — Normal</option>
                            <option value="2">2 — Complementar</option>
                            <option value="3">3 — Ajuste</option>
                            <option value="4">4 — Devolução</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Group>
                          <label className="nfe-dest-label">Tipo de Nota *</label>
                          <Form.Select size="sm" {...register('tipoNota')} disabled={isPending}>
                            <option value="0">0 — Entrada</option>
                            <option value="1">1 — Saída</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Group>
                          <label className="nfe-dest-label">Destino</label>
                          <Form.Select size="sm" {...register('indicadorDestinatario')} disabled={isPending}>
                            <option value="">—</option>
                            <option value="1">1 — Interno</option>
                            <option value="2">2 — Interestadual</option>
                            <option value="3">3 — Exterior</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Group>
                          <label className="nfe-dest-label">Consumidor Final</label>
                          <Form.Select size="sm" {...register('indicadorFinal')} disabled={isPending}>
                            <option value="">—</option>
                            <option value="0">0 — Normal</option>
                            <option value="1">1 — Consumidor final</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Group>
                          <label className="nfe-dest-label">Presença do Comprador</label>
                          <Form.Select size="sm" {...register('indicadorPresenca')} disabled={isPending}>
                            <option value="">—</option>
                            <option value="0">0 — Não se aplica</option>
                            <option value="1">1 — Presencial</option>
                            <option value="2">2 — Internet</option>
                            <option value="3">3 — Teleatendimento</option>
                            <option value="4">4 — Entrega em domicílio</option>
                            <option value="9">9 — Outros</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-3 pt-3 border-top border-opacity-25">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        loading={isPending}
                        loadingPosition="start"
                        className="d-flex align-items-center gap-2"
                      >
                        Criar Rascunho
                        {!isPending && <FontAwesomeIcon icon={faArrowRight} />}
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                {LOCKED_SECTIONS.map(s => (
                  <Accordion.Item key={s.num} eventKey={`locked-${s.num}`} className="nfe-accordion-item--locked">
                    <Accordion.Header>
                      <span className="nfe-sec-num">{s.num}</span>
                      {s.label}
                      <FontAwesomeIcon icon={faLock} className="ms-2 text-body-tertiary" style={{ fontSize: '0.6rem' }} />
                    </Accordion.Header>
                  </Accordion.Item>
                ))}
              </Accordion>

              <Card.Footer className="border-top border-opacity-25 py-2 px-3">
                <Button
                  variant="phoenix-secondary"
                  size="sm"
                  onClick={() => navigate('/operacional/nfe')}
                  disabled={isPending}
                  className="d-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Voltar
                </Button>
              </Card.Footer>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default NovaNFePage;
